import React, { useState, useEffect } from "react";
import { Box, Typography, Divider, TextField, CircularProgress } from "@mui/material";
import { OrderData } from "../types/order";
import InfoRow from "../common/InfoRow";
import { ModalWrapper } from "src/shared/components/ModalWrapper";
import CommonButtons from "src/shared/components/CommonButtons";

interface PaymentDialogProps {
  open: boolean;
  onClose: () => void;
  order: OrderData;
  paymentData: {
    discount: string;
    ReceivedAmount: string;
    paymentMethod: string;
  };
  onPaymentDataChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onProcessPayment: () => void;
  isProcessing: boolean;
}

const PaymentDialog: React.FC<PaymentDialogProps> = ({ open, onClose, order, paymentData, onPaymentDataChange, onProcessPayment, isProcessing }) => {
  const [localReceivedAmount, setLocalReceivedAmount] = useState("0");
  const [error, setError] = useState("");

  // Sync local state with parent data
  useEffect(() => {
    setLocalReceivedAmount(paymentData.ReceivedAmount || "0");
  }, [paymentData.ReceivedAmount]);

  const handleFocus = () => {
    if (localReceivedAmount === "0") setLocalReceivedAmount("");
    setError(""); // Clear error on focus
  };

  const handleBlur = () => {
    if (localReceivedAmount.trim() === "") {
      setLocalReceivedAmount("0");
      const syntheticEvent = {
        target: { name: "ReceivedAmount", value: "0" },
      } as React.ChangeEvent<HTMLInputElement>;
      onPaymentDataChange(syntheticEvent);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // If empty string, allow it for better UX during typing
    if (value === "") {
      setLocalReceivedAmount("");
      setError("");
      return;
    }

    // Parse the input value
    const enteredAmount = parseFloat(value);

    // Validate if it's a valid number
    if (isNaN(enteredAmount)) {
      setLocalReceivedAmount(value);
      setError("Please enter a valid number");
      return;
    }

    // Calculate old remaining amount
    const oldRemaining = parseFloat(order.RemainingAmount || order.totalAmount || "0");

    // Validate that received amount is not greater than remaining amount
    if (enteredAmount > oldRemaining) {
      setError(`Amount cannot exceed ${oldRemaining.toFixed(2)} QAR`);
      setLocalReceivedAmount(value); // Still update to show user input
    } else {
      setError("");
      setLocalReceivedAmount(value);
      onPaymentDataChange(e);
    }
  };

  // Calculate remaining amount dynamically
  const oldRemaining = parseFloat(order.RemainingAmount || order.totalAmount || "0");
  const enteredAmount = parseFloat(localReceivedAmount || "0");
  const newRemaining = Math.max(oldRemaining - enteredAmount, 0);

  // Check if received amount is valid (0 <= amount <= oldRemaining)
  const isValidAmount = enteredAmount >= 0 && enteredAmount <= oldRemaining && !isNaN(enteredAmount);

  if (!open) return null;

  return (
    <ModalWrapper title="Payment Summary" onClose={onClose} showBackButton showCloseButton onBack={onClose} width="max-w-lg">
      {/* Customer + Order Details */}
      <Box mb={2}>
        <InfoRow label="Customer Name" value={order.customer?.Name || "N/A"} />
        <InfoRow label="Order ID" value={order.Orderid} />
        <InfoRow label="Total Amount" value={`${order.totalAmount} QAR`} bold />
        <InfoRow label="Discount" value={`${order.discount || "0"} QAR`} />
        <InfoRow label="Previously Received" value={order.payments?.[0]?.ReceivedAmount ? `${order.payments[0].ReceivedAmount} QAR` : "0 QAR"} />
        <InfoRow label="Old Remaining" value={`${oldRemaining.toFixed(2)} QAR`} bold />
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Received Amount Input */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="subtitle1" component="p" sx={{ color: "text.secondary", fontWeight: 600 }}>
          Received Amount
        </Typography>
        <Box>
          <TextField
            name="ReceivedAmount"
            value={localReceivedAmount}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            size="small"
            type="number"
            sx={{ width: 150 }}
            inputProps={{
              style: { textAlign: "right" },
              min: 0,
              max: oldRemaining,
              step: "0.01",
            }}
            error={!!error}
            helperText={error}
          />
          <Typography variant="caption" sx={{ display: "block", textAlign: "right", color: "text.secondary", mt: 0.5 }}>
            Max: {oldRemaining.toFixed(2)} QAR
          </Typography>
        </Box>
      </Box>

      {/* Remaining */}
      <InfoRow label="Remaining Amount" value={`${newRemaining.toFixed(2)} QAR`} bold />

      {/* Footer Buttons */}
      <Divider sx={{ my: 3 }} />
      <Box className="flex justify-end gap-3 mt-4">
        <CommonButtons className="w-40" label="Cancel" variant="outlineDanger" onClick={onClose} disabled={isProcessing} />
        <CommonButtons
          label={isProcessing ? <CircularProgress size={24} color="inherit" /> : "Confirm Payment"}
          onClick={onProcessPayment}
          disabled={isProcessing || !isValidAmount || localReceivedAmount.trim() === "" || parseFloat(localReceivedAmount) <= 0}
        />
      </Box>
    </ModalWrapper>
  );
};

export default PaymentDialog;
